import { useRef, useState, type ReactNode } from "react";
import type { ApiClient } from "../../shared/api/client";
import type {
  AbilityRecord,
  CollectionManifest,
} from "../../shared/api/generated/contracts";
import { useT } from "../../shared/i18n";
import { Button, Card } from "../../shared/ui";
import { hiddenFieldIds, visibleFields, withoutKeys } from "../renderer/fields";
import { RecordForm, type ConflictState } from "./RecordForm";
import { ConflictSheet } from "./ConflictSheet";

function label(value: unknown, fallback: string): string {
  if (value === undefined) return fallback;
  return typeof value === "string" ? value : JSON.stringify(value);
}

function Surface({
  inSheet,
  title,
  children,
}: {
  inSheet: boolean;
  title: string | undefined;
  children: ReactNode;
}) {
  return inSheet ? (
    <div>{children}</div>
  ) : (
    <Card title={title}>{children}</Card>
  );
}

export function CollectionEditor({
  api,
  scope,
  collection,
  collections,
  records,
  onSaved,
  selectedRecord,
  onDeleted,
  localOnly = false,
  createDefaults,
  onClose,
}: {
  api: ApiClient;
  scope: {
    workspaceId: string;
    abilityId: string;
    actorId: string;
    schemaVersionId: string;
  };
  collection: CollectionManifest;
  collections: CollectionManifest[];
  records: AbilityRecord[];
  onSaved: (record: AbilityRecord) => void;
  selectedRecord?: AbilityRecord;
  onDeleted?: (id: string) => void;
  /** Draft Preview simulation: mutate only the caller-owned memory overlay. */
  localOnly?: boolean;
  /**
   * Create mode: open straight into a new record prefilled with these values,
   * hide the record list, and call `onClose` after save or cancel.
   */
  createDefaults?: Record<string, unknown>;
  onClose?: () => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState<AbilityRecord | "new" | null>(
    createDefaults ? "new" : (selectedRecord ?? null),
  );
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const request = useRef<{ fingerprint: string; key: string } | null>(null);
  const hiddenIds = hiddenFieldIds(collection);
  const fields = visibleFields(collection).map((field) => {
    if (field.type !== "reference") return field;
    const target = collections.find(
      (item) => item.id === field.config?.target_collection_id,
    );
    return {
      ...field,
      config: {
        ...field.config,
        options: records
          .filter((record) => record.collection_id === target?.id)
          .map((record) => ({
            value: record.id,
            label: label(
              record.payload[target?.primary_display_field_id ?? ""] ??
                record.id,
              record.id,
            ),
          })),
      },
    };
  });
  const seed = Object.fromEntries(
    Object.entries(createDefaults ?? {}).filter(([key]) =>
      visibleFields(collection).some((field) => field.id === key),
    ),
  );
  const cancel = () => {
    setEditing(null);
    onClose?.();
  };
  const inSheet = createDefaults !== undefined;
  const onRecordPage = selectedRecord !== undefined;
  return (
    <Surface
      inSheet={inSheet}
      title={onRecordPage ? undefined : collection.label}
    >
      {!selectedRecord && !createDefaults && (
        <>
          <Button
            onClick={() => {
              request.current = null;
              setEditing("new");
            }}
          >
            {t("record.new")}
          </Button>
          <ul>
            {records
              .filter((record) => record.collection_id === collection.id)
              .map((record) => (
                <li key={record.id}>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      request.current = null;
                      setEditing(record);
                    }}
                  >
                    {label(
                      record.payload[collection.primary_display_field_id] ??
                        record.id,
                      record.id,
                    )}{" "}
                    — {t("record.edit")}
                  </Button>
                </li>
              ))}
          </ul>
        </>
      )}
      {editing && (
        <>
          <RecordForm
            key={
              editing === "new"
                ? "new"
                : `${editing.id}:${String(editing.revision)}`
            }
            fields={fields}
            initial={
              editing === "new" ? seed : withoutKeys(editing.payload, hiddenIds)
            }
            onConflict={setConflict}
            onDelete={
              editing !== "new" && onDeleted
                ? async () => {
                    if (localOnly) {
                      onDeleted(editing.id);
                      setEditing(null);
                      return;
                    }
                    const fingerprint = `delete:${editing.id}:${String(editing.revision)}`;
                    if (request.current?.fingerprint !== fingerprint)
                      request.current = {
                        fingerprint,
                        key: crypto.randomUUID(),
                      };
                    await api.deleteRecord(scope, editing, request.current.key);
                    onDeleted(editing.id);
                    setEditing(null);
                    request.current = null;
                  }
                : undefined
            }
            onSubmit={async (payload) => {
              const current = editing === "new" ? undefined : editing;
              if (localOnly) {
                onSaved(
                  current
                    ? {
                        ...current,
                        payload: {
                          ...Object.fromEntries(
                            Object.entries(current.payload).filter(([key]) =>
                              hiddenIds.has(key),
                            ),
                          ),
                          ...payload,
                        },
                      }
                    : {
                        id: crypto.randomUUID(),
                        collection_id: collection.id,
                        revision: 0,
                        payload,
                      },
                );
                setEditing(null);
                onClose?.();
                return;
              }
              const fingerprint = JSON.stringify([
                current?.id,
                current?.revision,
                payload,
              ]);
              if (request.current?.fingerprint !== fingerprint)
                request.current = { fingerprint, key: crypto.randomUUID() };
              let saved: AbilityRecord;
              try {
                saved = await api.saveRecord(
                  scope,
                  collection.id,
                  payload,
                  request.current.key,
                  current,
                );
              } catch (error) {
                const cause =
                  error instanceof Error
                    ? (error.cause as { code?: string } | undefined)
                    : undefined;
                if (current && cause?.code === "stale_revision") {
                  const latest = await api.record(
                    scope.workspaceId,
                    scope.abilityId,
                    current.id,
                  );
                  throw Object.assign(new Error("stale_revision"), {
                    current: latest,
                    attempted: payload,
                  } satisfies ConflictState);
                }
                throw error;
              }
              onSaved(saved);
              setEditing(null);
              request.current = null;
              onClose?.();
            }}
            onCancel={createDefaults || onRecordPage ? cancel : undefined}
            bare={inSheet}
            stacked={onRecordPage}
          />
          {!createDefaults && !onRecordPage && (
            <Button variant="secondary" onClick={cancel}>
              {t("record.cancel")}
            </Button>
          )}
        </>
      )}
      {conflict && (
        <ConflictSheet
          open
          onOpenChange={(open) => {
            if (!open) setConflict(null);
          }}
          conflict={conflict}
          changedFields={fields
            .filter(
              (field) =>
                JSON.stringify(conflict.attempted[field.id]) !==
                JSON.stringify(conflict.current.payload[field.id]),
            )
            .map((field) => ({
              fieldId: field.id,
              label: field.label,
              mine: label(conflict.attempted[field.id], ""),
              server: label(conflict.current.payload[field.id], ""),
            }))}
          onReload={() => {
            setEditing(conflict.current);
            setConflict(null);
            request.current = null;
          }}
          onKeepMine={() => {
            setEditing({ ...conflict.current, payload: conflict.attempted });
            setConflict(null);
            request.current = null;
          }}
          onDiscardMine={() => {
            setEditing(null);
            setConflict(null);
            request.current = null;
          }}
        />
      )}
    </Surface>
  );
}
